import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { makeRequest } from "../../../../assets/functions_helper";
import ListData from "../../../../components/listData";
import {
  CustomDialog,
  CustomDialogActions,
  CustomDialogContent,
  CustomDialogTitle,
} from "../../../../components/customDialog";
import { CloseButton, SendButton } from "../../../../components/buttons";
import CreateCategory from "./create_categories";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ImageIcon from "@mui/icons-material/Image";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function ListCategories({ restaurantId }) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const [isLoaded, setIsLoaded] = useState(false);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryType, setCategoryType] = useState("categories"); // para el formulario
  const [idCategory, setIdCategory] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isEditFlow, setIsEditFlow] = useState(false);

  // Nuevo estado para el filtro por tipo
  const [selectedType, setSelectedType] = useState(null); // null = mostrar tarjetas

  const navigate = useNavigate();

  // Función para obtener categorías con filtro opcional
  const fetchCategories = useCallback(
    (type = null) => {
      let url = `menu_manager/categories/${restaurantId}`;
      if (type) {
        url += `?category_type=${type}`;
      }
      makeRequest(
        "GET",
        url,
        {},
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        null,
        () => navigate("/login")
      )
        .then((response) => {
          const mapped = response.map((cat) => ({
            id: cat.id,
            title: cat.name,
            title2: cat.description,
            originalData: cat,
          }));
          setCategories(mapped);
          setIsLoaded(true);
        })
        .catch((error) => console.error(error));
    },
    [restaurantId, navigate]
  );

  // Efecto: cuando se selecciona un tipo, cargamos los datos
  useEffect(() => {
    if (selectedType !== null) {
      setIsLoaded(false);
      fetchCategories(selectedType);
    } else {
      // Si no hay tipo seleccionado, no mostramos lista, pero podemos dejar isLoaded en true para mostrar tarjetas
      setIsLoaded(true);
      setCategories([]); // opcional, limpiar datos anteriores
    }
  }, [selectedType, fetchCategories]);

  const refreshStates = () => {
    setName("");
    setDescription("");
    setCategoryType("categories");
  };

  const handleSubmitCreateOrEditCategory = () => {
    let url = isEditFlow
      ? `menu_manager/categories/${restaurantId}/${idCategory}`
      : `menu_manager/categories/${restaurantId}`;
    let method = isEditFlow ? "PUT" : "POST";

    if (!name || !description || !categoryType) {
      setSnackbarMessage("Por favor completa todos los campos.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const body = { name, description, category_type: categoryType };

    makeRequest(
      method,
      url,
      JSON.stringify(body),
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      null,
      null,
      () => navigate("/login")
    )
      .then(() => {
        setSnackbarMessage(
          isEditFlow
            ? "Categoría editada correctamente"
            : "Categoría creada correctamente"
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setOpen(false);
        // Refrescar la lista actual (con el tipo seleccionado)
        if (selectedType) {
          fetchCategories(selectedType);
        }
        refreshStates();
      })
      .catch(() => {
        setSnackbarMessage(
          isEditFlow
            ? "Ocurrió un error al editar la categoría"
            : "Ocurrió un error al crear la categoría"
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  const handleDeleteCategory = (categoryId) => {
    setCategoryToDelete(categoryId);
    setOpenConfirmDelete(true);
  };

  const confirmDelete = () => {
    if (!categoryToDelete) return;

    makeRequest(
      "DELETE",
      `menu_manager/categories/${restaurantId}/${categoryToDelete}`,
      {},
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      null,
      null,
      () => navigate("/login")
    )
      .then(() => {
        setSnackbarMessage("Categoría eliminada correctamente");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        if (selectedType) {
          fetchCategories(selectedType);
        }
      })
      .catch(() => {
        setSnackbarMessage("Ocurrió un error al eliminar la categoría");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => {
        setOpenConfirmDelete(false);
        setCategoryToDelete(null);
      });
  };

  const handleOnCloseCreateOrEditCategory = () => {
    setName("");
    setDescription("");
    setCategoryType("categories");
    setIdCategory("");
    setIsEditFlow(false);
    setOpen(false);
  };

  const handleEditCategory = (category) => {
    setName(category.originalData.name);
    setDescription(category.originalData.description);
    setCategoryType(selectedType);
    setIdCategory(category.id);
    setIsEditFlow(true);
    setOpen(true);
  };

  // Manejadores para las tarjetas
  const handleSelectType = (type) => {
    setSelectedType(type);
    setCategoryType(type);
  };

  const handleBack = () => {
    setSelectedType(null);
  };

  // Estilos para las tarjetas
  const cardStyles = {
    card: {
      width: isSmall ? "90%" : 300,
      height: 200,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
      transition: "transform 0.2s, box-shadow 0.2s",
      borderRadius: 4,
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      backgroundColor: "#ffffff",
      "&:hover": {
        transform: "scale(1.05)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
      },
    },
    icon: {
      fontSize: 80,
      color: theme.palette.primary.main,
      marginBottom: 2,
    },
    title: {
      fontSize: 24,
      fontWeight: 600,
      color: "#333333",
    },
  };

  return (
    <>
      {isLoaded ? (
        <>
          {selectedType === null ? (
            // Pantalla de selección de tipo
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "70vh",
                gap: 4,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#1A1A1A", mb: 2 }}>
                Selecciona el tipo de categoría
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isSmall ? "column" : "row",
                  gap: 4,
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                {/* Tarjeta Productos */}
                <Card
                  sx={cardStyles.card}
                  onClick={() => handleSelectType("categories")}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    <ShoppingCartIcon sx={cardStyles.icon} />
                    <Typography sx={cardStyles.title}>Productos</Typography>
                  </CardContent>
                </Card>

                {/* Tarjeta Imágenes */}
                <Card
                  sx={cardStyles.card}
                  onClick={() => handleSelectType("background_images_categories")}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    <ImageIcon sx={cardStyles.icon} />
                    <Typography sx={cardStyles.title}>Imágenes</Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          ) : (
            // Lista de categorías filtradas
            <>
              {/* Barra superior con botón de volver */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "95%",
                  margin: "20px auto 10px auto",
                }}
              >
                <IconButton
                  onClick={handleBack}
                  sx={{
                    backgroundColor: "#f0f0f0",
                    "&:hover": { backgroundColor: "#e0e0e0" },
                    padding: 1,
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "#1A1A1A" }}>
                  {selectedType === "categories" ? "Categorías de Productos" : "Categorías de Imágenes"}
                </Typography>
                <Box sx={{ width: 40 }} /> {/* Espaciador */}
              </Box>

              <ListData
                title="" // El título ya lo mostramos arriba
                data={categories}
                setData={setCategories}
                edit={true}
                editFunction={(id, title, public_id, item) =>
                  handleEditCategory(item)
                }
                create={true}
                createMessage="Crear Categoría"
                createFunction={() => setOpen(true)}
                nameColumn1="Nombre"
                nameColumn2="Descripción"
                nameColumn3="Acciones"
                deleted={true}
                deletedFunction={(id) => handleDeleteCategory(id)}
              />
            </>
          )}

          {/* Diálogo para crear/editar categoría (sin cambios) */}
          <CustomDialog
            open={open}
            onClose={() => handleOnCloseCreateOrEditCategory()}
          >
            <CustomDialogTitle>
              {isEditFlow ? "Editar Categoría" : "Crear Categoría"}
            </CustomDialogTitle>
            <CustomDialogContent>
              <CreateCategory
                name={name}
                setName={setName}
                description={description}
                setDescription={setDescription}
                categoryType={categoryType}
                setCategoryType={setCategoryType}
                classification={selectedType}
              />
            </CustomDialogContent>
            <CustomDialogActions>
              <CloseButton
                onClick={() => handleOnCloseCreateOrEditCategory()}
                sx={{
                  backgroundColor: "#E0E0E0",
                  color: "#424242",
                  "&:hover": {
                    backgroundColor: "#D5D5D5",
                  },
                }}
              >
                Cerrar
              </CloseButton>
              <SendButton onClick={handleSubmitCreateOrEditCategory}>
                Enviar
              </SendButton>
            </CustomDialogActions>
          </CustomDialog>

          {/* Diálogo de confirmación para eliminar */}
          <CustomDialog
            open={openConfirmDelete}
            onClose={() => setOpenConfirmDelete(false)}
          >
            <CustomDialogTitle>Confirmar eliminación</CustomDialogTitle>
            <CustomDialogContent>
              ¿Estás seguro que deseas eliminar esta categoría? Esta acción no se
              puede deshacer.
            </CustomDialogContent>
            <CustomDialogActions>
              <CloseButton
                onClick={() => setOpenConfirmDelete(false)}
                sx={{
                  backgroundColor: "#E0E0E0",
                  color: "#424242",
                  "&:hover": {
                    backgroundColor: "#D5D5D5",
                  },
                }}
              >
                Cancelar
              </CloseButton>
              <SendButton onClick={confirmDelete} color="error">
                Eliminar
              </SendButton>
            </CustomDialogActions>
          </CustomDialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={4000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSnackbarOpen(false)}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </>
      ) : (
        <Box sx={{ height: "100vh" }} />
      )}
    </>
  );
}

export default ListCategories;